import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/config/public").then(r => setPlans(r.data.plans));
    api.get("/subscription/billing").then(r => setBilling(r.data.payments)).catch(() => {});
  }, []);

  const subscribe = async (plan_id) => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/subscribe", { plan_id });
      toast.success(`Subscribed to ${plan_id}. ${data.notice}`);
      await refresh();
      const r = await api.get("/subscription/billing");
      setBilling(r.data.payments);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const cancel = async () => {
    await api.post("/subscription/cancel");
    toast("Subscription cancelled. You still have access to your saved data.");
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Subscription</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Current plan: <span className="font-semibold capitalize">{user?.subscription_status}</span>{user?.plan_id ? ` · ${user.plan_id}` : ""}</p>
        {user?.next_billing_at && <p className="text-xs text-slate-500 mt-1">Next billing: {new Date(user.next_billing_at).toLocaleDateString()}</p>}
      </div>

      <div className="rounded-2xl border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-sm">
        <span className="font-semibold text-amber-700 dark:text-amber-400">Note:</span> Payments are <span className="font-mono">MOCKED</span>. Configure Stripe secrets (`STRIPE_API_KEY`, `STRIPE_WEBHOOK_SECRET`) to enable real billing.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map(p => (
          <div key={p.plan_id} className={`rounded-2xl border p-6 relative ${p.recommended ? "border-2 border-amber-500 bg-white dark:bg-slate-900" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"}`}>
            {p.recommended && <div className="pill bg-amber-500 text-slate-950 absolute -top-3 left-6"><Sparkles className="h-3 w-3" /> Recommended</div>}
            {p.badge && !p.recommended && <div className="pill bg-slate-900 text-white absolute -top-3 left-6">{p.badge}</div>}
            <div className="text-xs uppercase tracking-widest text-slate-500">{p.name}</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-4xl font-extrabold mono">${p.price}</span>
              <span className="text-sm text-slate-500">/{p.billing_period}</span>
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {p.features?.map((f,i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />{f}</li>
              ))}
            </ul>
            <button data-testid={`subscribe-${p.plan_id}`} onClick={() => subscribe(p.plan_id)} disabled={loading || user?.plan_id === p.plan_id} className={`${p.recommended ? "btn-accent" : "btn-primary"} mt-6 w-full disabled:opacity-60`}>
              {user?.plan_id === p.plan_id ? "Current plan" : "Subscribe (mock)"}
            </button>
          </div>
        ))}
      </div>

      {user?.subscription_status === "active" && (
        <button data-testid="cancel-sub-btn" onClick={cancel} className="btn-outline">Cancel subscription</button>
      )}

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <h3 className="text-lg font-semibold">Billing history</h3>
        {billing.length === 0 ? <div className="text-sm text-slate-500 mt-2">No payments yet.</div> :
          <ul className="mt-3 divide-y divide-slate-200 dark:divide-slate-800">{billing.map(b => (
            <li key={b.payment_id} className="py-2 flex items-center justify-between text-sm">
              <div><div className="font-semibold">{b.plan_id}</div><div className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</div></div>
              <div className="mono">${b.amount}</div>
            </li>
          ))}</ul>}
      </div>
    </div>
  );
}
